import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { animated } from 'react-spring';
import { Text } from '@brandwatch/axiom-components';

import auth from '../auth';
import { DeviceTypeContext } from '../DeviceTypeContext.jsx';
import ViziaComponentAnimator from './ViziaComponentAnimator.jsx';
import { observe } from '../lib/intersectionObserver';
import './ViziaComponent.css';

const cacheBuster = Date.now().toString(36);

const ContentWrapper = props => (
  <div className="ViziaComponent--wrapper">
    {props.iframe}
    {props.cover}
  </div>
);

const ViziaComponentHeader = props => (
  <div className="ViziaComponentHeader vc-tile-bg sc ax-theme--night vc-text-neutral">
    <Text textStrong textSize="body">
      {props.name}
    </Text>
  </div>
);

const ViziaComponentCover = props => (
  <div
    className="ViziaComponent--cover"
    onClick={() =>
      props.onClick({
        type: 'InteractiveComponent',
        id: props.id,
        customAnimation: true
      })
    }
  />
);

class ViziaComponent extends Component {
  constructor(props) {
    super(props);
    this.el = createRef();
    this.iframe = null;
    this.state = { renderIframe: false, preloadState: null };

    this.setIframeRef = this.setIframeRef.bind(this);
  }

  buildConfig(profile) {
    return {
      is_public: typeof this.props.id === 'string',
      colors: this.props.colors,
      nsfw: this.props.folderOptions.nsfw,
      locale: this.props.folderOptions.locale,
      themeId: this.props.folderOptions.themeId,
      timezone: this.props.folderOptions.timezone,
      scene: {
        slide_id: this.props.slide_id,
        id: this.props.id,
        name: this.props.name,
        options: this.props.options,
        component_type_id: this.props.component_type_id,
        type: this.props.component_type_id,
        serverside: this.props.serverside
      },
      bwApiRootUrl: this.props.config.bwApiRootUrl.href,
      imagecheckRootUrl: this.props.config.imageCheckRootUrl.href,
      token: profile.api2AccessToken,
      viewportUnits: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      isInteractive: this.props.isInteractive
    };
  }

  onIframeLoad(iframe) {
    const loadIframe = profile => {
      const config = this.buildConfig(profile);

      if (!iframe || !iframe.contentWindow) {
        return;
      }

      iframe.contentWindow.postMessage(
        {
          config,
          preloadState: this.state.preloadState || this.props.preloadState
        },
        this.props.config.bundleLoaderUrl.origin
      );
    };

    if (typeof this.props.id === 'string' || !auth.hasStore()) {
      loadIframe({});
    } else {
      auth.getUserProfile().then(loadIframe);
    }
  }

  setIframeRef(iframe) {
    if (iframe && !this.iframe) {
      iframe.addEventListener('load', () => this.onIframeLoad(iframe));
      this.iframe = iframe;
    }
  }

  shouldComponentUpdate(nextProps) {
    if (nextProps.preloadState !== this.props.preloadState && this.iframe) {
      this.iframe.contentWindow.postMessage(
        { preloadState: nextProps.preloadState },
        this.props.config.bundleLoaderUrl.origin
      );
      return false;
    }
    return true;
  }

  componentDidMount() {
    if (!this.context.isMobile) {
      return;
    }
    observe(this.el.current, () => {
      if (!this.iframe) {
        this.setState({ loadIframes: true, preloadState: 'bundle_start' });
      } else {
        this.iframe.contentWindow.postMessage(
          { preloadState: 'bundle_start' },
          this.props.config.bundleLoaderUrl.origin
        );
      }
    });
  }

  render() {
    if (!this.props.component_type_id) {
      return (
        <ViziaComponentAnimator id={this.props.id}>
          {() => (
            <animated.div
              id={`component-${this.props.id}`}
              ref={this.el}
              className="ViziaComponent"
            />
          )}
        </ViziaComponentAnimator>
      );
    }

    const iframeUrl = new URL(this.props.config.bundleLoaderUrl.href);

    iframeUrl.searchParams.set('bundle', this.props.component_type_id);
    iframeUrl.searchParams.set('cachebuster', cacheBuster);

    if (this.props.folderOptions.themeId) {
      iframeUrl.searchParams.set('themeId', this.props.folderOptions.themeId);
    }

    if (this.props.otp) {
      iframeUrl.searchParams.set('otp', this.props.otp);
    }

    iframeUrl.searchParams.set('preloading', true);

    const iframe =
      this.props.loadIframes || this.state.loadIframes ? (
        <iframe
          src={iframeUrl.href}
          className="BundleLoader"
          ref={this.setIframeRef}
        />
      ) : null;

    return (
      <ViziaComponentAnimator id={this.props.id}>
        {({ style, showCover, showHeader }) => {
          const cover = showCover && (
            <ViziaComponentCover
              onClick={this.props.onClick}
              id={this.props.id}
            />
          );

          return (
            <animated.div
              style={style}
              id={`component-${this.props.id}`}
              ref={this.el}
              className="ViziaComponent"
            >
              {showHeader && this.props.show_header && <ViziaComponentHeader name={this.props.name} />}
              <ContentWrapper
                component_type_id={this.props.component_type_id}
                cover={cover}
                iframe={iframe}
              />
            </animated.div>
          );
        }}
      </ViziaComponentAnimator>
    );
  }
}

ViziaComponent.contextType = DeviceTypeContext;

ViziaComponent.defaultProps = {
  loadIframes: false,
  name: '',
  folderOptions: {},
  colors: {
    palette: {},
    query: {},
    category: {}
  },
  onClick: () => {},
  isInteractive: true
};

ViziaComponent.propTypes = {
  loadIframes: PropTypes.bool,
  id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  name: PropTypes.string,
  options: PropTypes.object,
  serverside: PropTypes.string,
  component_type_id: PropTypes.string,
  folderOptions: PropTypes.object,
  config: PropTypes.object,
  slide_id: PropTypes.string,
  colors: PropTypes.object,
  preloadState: PropTypes.string,
  onClick: PropTypes.func,
  isInteractive: PropTypes.bool
};

export default ViziaComponent;
